import * as React from "react";
import List from "@mui/material/List";
import {Box, styled, useTheme} from "@mui/material";
import {ExpandMore, ChevronRight} from "@mui/icons-material";
import {
    TreeItem2Content,
    TreeItem2IconContainer,
    TreeItem2Root,
    TreeItem2GroupTransition,
} from "@mui/x-tree-view/TreeItem2";
import Typography from "@mui/material/Typography";
import {
    useTreeItem2,
    UseTreeItem2Parameters,
} from "@mui/x-tree-view/useTreeItem2";
import {SvgIconProps} from "@mui/material/SvgIcon";
import {
    SimpleTreeView,
    TreeItem2Icon,
    TreeItem2Provider,
} from "@mui/x-tree-view";
import clsx from "clsx";
import {useAuthRoutes, useMenuActions} from "@/store/menu.c";
import {useEffect, useMemo, useState} from "react";
import {AuthRouteVO} from "@/service/menu.s";
import {useRouter} from "@/helper/hooks";
import isEmpty from "lodash/isEmpty";
import {useLocation} from "react-router-dom";
import {IconMap} from "@/widget/IconMap/IconMap";

interface StyledTreeItemProps
    extends Omit<UseTreeItem2Parameters, "rootRef">,
        React.HTMLAttributes<HTMLLIElement> {
    bgColor?: string;
    bgColorForDarkMode?: string;
    color?: string;
    colorForDarkMode?: string;
    labelIcon: React.ElementType<SvgIconProps> | 'span';
    labelInfo?: string;
}

const CustomTreeItemRoot = styled(TreeItem2Root)(({theme}) => ({
    color: theme.palette.text.secondary,
    padding: 0,
}));

const CustomTreeItemContent = styled(TreeItem2Content)(({theme}) => ({
    marginBottom: theme.spacing(0.1),
    color: theme.palette.text.secondary,
    borderRadius: theme.spacing(1),
    paddingY: theme.spacing(0.3),
    paddingLeft: theme.spacing(0),
    flexDirection: "row-reverse",
    gap: 0,
    fontWeight: theme.typography.fontWeightMedium,
    "&.expanded": {
        fontWeight: theme.typography.fontWeightRegular,
    },
    "&:hover": {
        backgroundColor: theme.palette.action.hover,
    },
    "&.focused, &.selected, &.selected.focused": {
        backgroundColor: `var(--tree-view-bg-color, ${theme.palette.action.selected})`,
        fontWeight: 550,
        color: "var(--tree-view-color)",
    },
}));

type ExpandedItemType = {
    name: string;
    path: string;
}

const CustomTreeItemIconContainer = styled(TreeItem2IconContainer)(
    ({theme}) => ({
        marginRight: theme.spacing(0),
        width: 4,
    }),
);

const CustomTreeItem = React.forwardRef(function CustomTreeItem(
    props: StyledTreeItemProps,
    ref: React.Ref<HTMLLIElement>,
) {
    const theme = useTheme();
    const {
        id,
        itemId,
        label,
        disabled,
        children,
        bgColor,
        color,
        labelIcon: LabelIcon,
        labelInfo,
        colorForDarkMode,
        bgColorForDarkMode,
        ...other
    } = props;

    const {
        getRootProps,
        getContentProps,
        getIconContainerProps,
        getLabelProps,
        getGroupTransitionProps,
        status,
    } = useTreeItem2({id, itemId, children, label, disabled, rootRef: ref});

    const style = {
        "--tree-view-color":
            theme.palette.mode !== "dark" ? color : colorForDarkMode,
        "--tree-view-bg-color":
            theme.palette.mode !== "dark" ? bgColor : bgColorForDarkMode,
    };

    return (
        <TreeItem2Provider itemId={itemId}>
            <CustomTreeItemRoot {...getRootProps({...other, style})}>
                <CustomTreeItemContent
                    {...getContentProps({
                        className: clsx("content", {
                            expanded: status.expanded,
                            selected: status.selected,
                            focused: status.focused,
                        }),
                    })}
                >
                    <CustomTreeItemIconContainer {...getIconContainerProps()}>
                        <TreeItem2Icon status={status}/>
                    </CustomTreeItemIconContainer>
                    <Box
                        sx={{
                            display: "flex",
                            flexGrow: 1,
                            alignItems: "center",
                            p: 0.5,
                            pr: 0,
                        }}
                    >
                        {/*47536bd6*/}
                        <Box component={LabelIcon} color="inherit" sx={{
                            mr: 1, "&.MuiSvgIcon-root": {
                                fill: "#47536bdb",
                            }
                        }}/>
                        <Typography
                            {...getLabelProps({
                                variant: "body2",
                                sx: {display: "flex", fontWeight: "inherit", flexGrow: 1},
                            })}
                        />
                        <Typography variant="caption" color="inherit">
                            {labelInfo}
                        </Typography>
                    </Box>
                </CustomTreeItemContent>
                {children && (
                    <TreeItem2GroupTransition {...getGroupTransitionProps()} />
                )}
            </CustomTreeItemRoot>
        </TreeItem2Provider>
    );
});

function EndIcon() {
    return <div style={{width: 24}}/>;
}

export default function MenuContent() {
    const authRoutes = useAuthRoutes();
    const {setBreadcrumbs} = useMenuActions();
    const router = useRouter();
    const {pathname} = useLocation();
    const [selected, setSelected] = useState<string | null>(null);
    const [expanded, setExpanded] = useState<string[]>([]);

    const expandedItems = (data: AuthRouteVO[], pathname: string): ExpandedItemType[] | undefined => {
        for (let i: number = 0; i < data.length; i++) {
            if (data[i] && data[i].path === pathname) {
                return [{name: data[i].menuName, path: data[i].path}];
            }
            if (data[i] && data[i].children) {
                const d: ExpandedItemType[] | undefined = expandedItems(data[i].children!, pathname);
                if (d) {
                    return d.concat({name: data[i].menuName, path: data[i].path});
                }
            }
        }
    };


    const focusRouting = (pathname: string, items: ExpandedItemType[]) => {
        setExpanded(items.map((item: ExpandedItemType): string => item.path));
        setSelected(pathname);
    }

    useEffect(() => {
        if (!isEmpty(authRoutes)) {
            const items = expandedItems(authRoutes, pathname);
            if (items) {
                focusRouting(pathname, items)
                items?.reverse();
                setBreadcrumbs(items);
            }
        }
    }, [pathname, authRoutes]);

    const renderRoutes = useMemo(() => {
        const func = (datas: AuthRouteVO[]) => {
            return datas?.map((data: AuthRouteVO,) => {
                const children = data.children || [];
                const path = data?.path;
                return (
                    <CustomTreeItem
                        key={path}
                        itemId={path}
                        label={data.menuName}
                        {...!isEmpty(children) ? {labelIcon: IconMap[data.source]} : {labelIcon: 'span'}}
                        // labelIcon={!isEmpty(children) ? MailOutlinedIcon : undefined}
                        onClick={() => {
                            if (isEmpty(children)) {
                                router.push(path);
                            }
                        }}
                    >
                        {func(children)}
                    </CustomTreeItem>
                );
            });
        };
        return func(authRoutes);
    }, [authRoutes]);

    return (
        <List>
            <SimpleTreeView
                expandedItems={expanded}
                selectedItems={selected}
                onExpandedItemsChange={(_, itemIds) => {
                    setExpanded(itemIds);
                }}
                onSelectedItemsChange={(_, itemIds: string | null) => {
                    setSelected(itemIds);
                }}
                slots={{
                    expandIcon: ChevronRight,
                    collapseIcon: ExpandMore,
                    endIcon: EndIcon,
                }}
                sx={{flexGrow: 1, maxWidth: 400}}
            >
                {renderRoutes}
            </SimpleTreeView>
        </List>
    );
}
